import { useCallback, useEffect, useRef, useState, type MutableRefObject } from "react";
import {
  getNextPresignedUrl,
  saveChunkMeta,
  markRecordingComplete,
} from "@/api/s3RecordingApi";

type UploadedChunkInfo = { index: number; url: string };

type RecorderErrorType = "general" | "s3_recording_fail";

export interface RecorderError {
  type: RecorderErrorType;
  message?: string;
  chunkIndex?: number;
  retryCount?: number;
}

export type VideoMimeType = "video/webm; codecs=vp8,opus";

export type Kind = "interview" | "demo";

interface UseRecorderOptions {
  recorderId?: string;
  chunkDurationMs?: number;
  mimeType?: VideoMimeType;
  kind?: Kind;
  onError?: (error: RecorderError) => void;
}

interface UseRecorderReturn {
  requestShareScreen: () => Promise<void>;
  adoptExistingScreenStream: (displayStream: MediaStream) => Promise<void>;
  startRecording: (recordingId: string) => void;
  stopRecording: () => Promise<void>;
  stopAndMarkComplete: () => Promise<void>;
  audioContext: AudioContext | null;
  destination: MediaStreamAudioDestinationNode | null;
  isSharing: boolean;
  isRecording: boolean;
  error?: RecorderError | null;
  screenStream: MediaStream | null;
  videoRef: MutableRefObject<HTMLVideoElement | null>;
  lastUploaded?: UploadedChunkInfo | null;
  attachAuxAudioStream: (stream: MediaStream | null) => void;
  attachAudioElement: (element: HTMLMediaElement | null) => void;
}

const DEFAULT_CHUNK_DURATION_MS = 10_000;
const DEFAULT_VIDEO_MIME_TYPE: VideoMimeType = "video/webm; codecs=vp8,opus";

const deriveFileExtensionFromMime = (mime: string): string => {
  const extension = mime.match(/video\/([a-z0-9]+)/i)?.[1];
  return extension ?? "mp4";
};

const useRecorder = (options: UseRecorderOptions = {}): UseRecorderReturn => {
  const {
    recorderId,
    chunkDurationMs = DEFAULT_CHUNK_DURATION_MS,
    mimeType = DEFAULT_VIDEO_MIME_TYPE,
    kind = "interview",
    onError,
  } = options || {};

  const fileExtension = deriveFileExtensionFromMime(mimeType);
  const [isSharing, setIsSharing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recorderError, setRecorderError] = useState<RecorderError | null>(null);
  const [lastUploaded, setLastUploaded] = useState<UploadedChunkInfo | null>(null);

  const displayMediaStreamRef = useRef<MediaStream | null>(null);
  const microphoneMediaStreamRef = useRef<MediaStream | null>(null);
  const compositeStreamRef = useRef<MediaStream | null>(null);
  const auxAudioStreamRef = useRef<MediaStream | null>(null);
  const auxConnectionsRef = useRef<AudioNode[]>([]);
  const audioContextInternalRef = useRef<AudioContext | null>(null);
  const destinationNodeRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const pendingAudioElementsRef = useRef<HTMLMediaElement[]>([]);
  const attachedElementsRef = useRef<WeakSet<HTMLMediaElement>>(new WeakSet());
  const [audioContextValue, setAudioContextValue] = useState<AudioContext | null>(null);
  const [destinationNodeValue, setDestinationNodeValue] = useState<MediaStreamAudioDestinationNode | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const pendingChunkBufferRef = useRef<Blob[]>([]);
  const chunkUploadChainRef = useRef<Promise<void>>(Promise.resolve());
  const activeRecordingIdRef = useRef<string>(recorderId ?? "");
  const lastChunkTimestampRef = useRef<number>(0);
  const recordingStartTimeRef = useRef<number>(0);
  const dataRequestTimerRef = useRef<number | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hasUploadedChunksRef = useRef(false);

  const attachAuxAudioStream = (stream: MediaStream | null) => {
    auxAudioStreamRef.current = stream;
    const ctx = audioContextInternalRef.current;
    const dest = destinationNodeRef.current;
    if (!ctx || !dest) return;

    auxConnectionsRef.current.forEach((node) => {
      try {
        node.disconnect();
      } catch {
        // ignore
      }
    });
    auxConnectionsRef.current = [];

    if (stream) {
      auxConnectionsRef.current = connectStreamToDestination(stream, ctx, dest, 1);
    }
  };

  const connectAudioElementToGraph = (
    element: HTMLMediaElement,
    ctx: AudioContext,
    dest: MediaStreamAudioDestinationNode
  ) => {
    if (attachedElementsRef.current.has(element)) {
      return; // Already attached
    }
    attachedElementsRef.current.add(element);

    void ensureAudioContextRunning(ctx);

    const source = ctx.createMediaElementSource(element);
    const gain = ctx.createGain();
    gain.gain.value = 1;
    source.connect(gain);
    gain.connect(dest);
    gain.connect(ctx.destination);
    auxConnectionsRef.current.push(source, gain);

    const cleanup = () => {
      const nodes = [source, gain];
      nodes.forEach((node) => {
        try {
          node.disconnect();
        } catch {
          // ignore disconnect errors
        }
      });
      auxConnectionsRef.current = auxConnectionsRef.current.filter(
        (node) => !nodes.some((n) => n === node)
      );
      element.removeEventListener("ended", cleanup);
      element.removeEventListener("error", cleanup);
    };

    element.addEventListener("ended", cleanup);
    element.addEventListener("error", cleanup);
  };

  const processPendingAudioElements = () => {
    const ctx = audioContextInternalRef.current;
    const dest = destinationNodeRef.current;
    if (!ctx || !dest) return;

    const pending = pendingAudioElementsRef.current;
    pendingAudioElementsRef.current = [];
    pending.forEach((el) => {
      connectAudioElementToGraph(el, ctx, dest);
    });
  };

  const attachAudioElement = useCallback((element: HTMLMediaElement | null) => {
    if (!element) return;
    const ctx = audioContextInternalRef.current;
    const dest = destinationNodeRef.current;
    
    if (!ctx || !dest) {
      // Audio context not ready yet, queue for later
      if (!pendingAudioElementsRef.current.includes(element)) {
        pendingAudioElementsRef.current.push(element);
      }
      return;
    }

    connectAudioElementToGraph(element, ctx, dest);
  }, []);

  const publishRecorderError = (errorToPublish: RecorderError) => {
    setRecorderError(errorToPublish);
    onError?.(errorToPublish);
  };

  const clearRecorderError = () => {
    setRecorderError(null);
  };

  useEffect(() => {
    const previewStream = compositeStreamRef.current || displayMediaStreamRef.current;
    if (videoRef.current && previewStream) {
      videoRef.current.srcObject = previewStream;
    }
  }, [isSharing]);

  useEffect(() => {
    if (recorderId) {
      activeRecordingIdRef.current = recorderId;
    }
  }, [recorderId]);

  const captureDisplayStream = async (): Promise<MediaStream> => {
    const constraints: DisplayMediaStreamOptions & { monitorTypeSurfaces?: "include" } = {
      video: { displaySurface: "monitor" },
      audio: false,
      monitorTypeSurfaces: "include",
    };
    if (!navigator.mediaDevices?.getDisplayMedia) {
      throw new Error("Screen sharing is not supported in this browser");
    }
    return navigator.mediaDevices.getDisplayMedia(constraints);
  };

  const captureMicrophoneStream = async (): Promise<MediaStream> => {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("Microphone capture is not supported in this browser");
    }
    return navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
      },
    });
  };

  const ensureAudioContextRunning = async (ctx: AudioContext) => {
    try {
      if (ctx.state !== "running") {
        await ctx.resume();
      }
    } catch {
      // resume rejection should not break recording
    }
  };

  const connectStreamToDestination = (
    stream: MediaStream,
    ctx: AudioContext,
    destinationNode: MediaStreamAudioDestinationNode,
    gainValue: number = 1
  ): AudioNode[] => {
    const connected: AudioNode[] = [];
    stream.getAudioTracks().forEach((track) => {
      const source = ctx.createMediaStreamSource(new MediaStream([track]));
      const gain = ctx.createGain();
      gain.gain.value = gainValue;
      source.connect(gain);
      gain.connect(destinationNode);
      connected.push(source, gain);
    });
    return connected;
  };

  const createAudioMixingGraph = async (
    microphoneStream: MediaStream,
    displayStream: MediaStream
  ): Promise<{ ctx: AudioContext; dest: MediaStreamAudioDestinationNode }> => {
    const ctx = new AudioContext({ latencyHint: "interactive" });
    await ensureAudioContextRunning(ctx);
    ctx.onstatechange = () => {
      if (ctx.state === "suspended") {
        void ensureAudioContextRunning(ctx);
      }
    };
    const destinationNode = ctx.createMediaStreamDestination();

    connectStreamToDestination(microphoneStream, ctx, destinationNode, 1.1);

    if (displayStream.getAudioTracks().length > 0) {
      connectStreamToDestination(displayStream, ctx, destinationNode, 0.9);
    }

    if (auxAudioStreamRef.current) {
      auxConnectionsRef.current = connectStreamToDestination(
        auxAudioStreamRef.current,
        ctx,
        destinationNode,
        1
      );
    }

    return { ctx, dest: destinationNode };
  };

  const buildCompositeRecorderStream = (
    displayStream: MediaStream,
    destinationNode: MediaStreamAudioDestinationNode
  ) => {
    const destinationAudioTrack = destinationNode.stream.getAudioTracks()[0] || null;
    return new MediaStream([
      ...displayStream.getVideoTracks(),
      ...(destinationAudioTrack ? [destinationAudioTrack] : []),
    ]);
  };

  const requestShareScreen = async () => {
    try {
      const displayStream = await captureDisplayStream();
      const microphoneStream = await captureMicrophoneStream();

      const { ctx, dest } = await createAudioMixingGraph(microphoneStream, displayStream);
      const compositeStream = buildCompositeRecorderStream(displayStream, dest);

      displayMediaStreamRef.current = displayStream;
      microphoneMediaStreamRef.current = microphoneStream;
      compositeStreamRef.current = compositeStream;
      audioContextInternalRef.current = ctx;
      destinationNodeRef.current = dest;

      setAudioContextValue(ctx);
      setDestinationNodeValue(dest);

      // Process any TTS audio elements that were queued before context was ready
      processPendingAudioElements();

      setIsSharing(true);
      if (videoRef.current) {
        videoRef.current.srcObject = compositeStreamRef.current;
      }
    } catch (err: any) {
      const errorName = err?.name || "UnknownError";
      let message = "Failed to start screen sharing.";
      if (errorName === "NotAllowedError") message += " Please allow screen/microphone permissions and Share Audio.";
      else if (errorName === "NotFoundError") message += " No shareable screen/microphone source available.";
      else if (errorName === "NotSupportedError") message += " Screen/system audio capture may not be supported in this browser.";
      publishRecorderError({ type: "general", message });
      throw err;
    }
  };

  const adoptExistingScreenStream = useCallback(async (existingDisplayStream: MediaStream) => {
    try {
      const microphoneStream = await captureMicrophoneStream();
      const { ctx, dest } = await createAudioMixingGraph(microphoneStream, existingDisplayStream);
      const compositeStream = buildCompositeRecorderStream(existingDisplayStream, dest);

      displayMediaStreamRef.current = existingDisplayStream;
      microphoneMediaStreamRef.current = microphoneStream;
      compositeStreamRef.current = compositeStream;
      audioContextInternalRef.current = ctx;
      destinationNodeRef.current = dest;

      setAudioContextValue(ctx);
      setDestinationNodeValue(dest);

      // Process any TTS audio elements that were queued before context was ready
      processPendingAudioElements();

      setIsSharing(true);
      if (videoRef.current) {
        videoRef.current.srcObject = compositeStreamRef.current;
      }
    } catch (err: any) {
      // Fallback: record the provided display stream only (no mixed mic/system audio)
      displayMediaStreamRef.current = existingDisplayStream;
      compositeStreamRef.current = existingDisplayStream;
      setIsSharing(true);
      if (videoRef.current) {
        videoRef.current.srcObject = existingDisplayStream;
      }
      publishRecorderError({
        type: "general",
        message: err?.message || "Failed to adopt existing screen stream with audio; recording display-only.",
      });
    }
  }, []);

  const getNextPresignedUrlInternal = async (): Promise<{ url: string; index: number }> => {
    if (!activeRecordingIdRef.current) {
      throw new Error("Recording not initialized");
    }
    const presignResponse = await getNextPresignedUrl({
      recordingId: activeRecordingIdRef.current,
      kind,
      fileType: fileExtension,
    });
    return { url: presignResponse.url, index: presignResponse.index };
  };

  const saveUploadedChunk = async (url: string, index: number, size: number) => {
    const sanitizedUrl = url.split("?")[0] ?? "";
    await saveChunkMeta({
      recordingId: activeRecordingIdRef.current,
      kind,
      index,
      url: sanitizedUrl,
      size,
    });
  };

  const uploadChunk = async (blob: Blob) => {
    const { url, index } = await getNextPresignedUrlInternal();
    if (!url || typeof url !== "string") {
      throw new Error("Invalid presigned URL returned by server");
    }

    const attemptUploadWithRetry = async (maxRetries = 2, baseDelayMs = 600) => {
      let attemptCount = 0;
      let lastError: unknown = null;
      while (attemptCount <= maxRetries) {
        try {
          const response = await fetch(url, {
            method: "PUT",
            mode: "cors",
            headers: { "Content-Type": blob.type || mimeType },
            body: blob,
          });
          if (!response.ok) throw new Error(`S3 PUT failed: ${response.status}`);
          return true;
        } catch (e) {
          lastError = e;
          if (attemptCount === maxRetries) break;
          const jitterMs = Math.floor(Math.random() * 150);
          const delayMs = baseDelayMs * Math.pow(2, attemptCount) + jitterMs;
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
        attemptCount += 1;
      }
      throw lastError;
    };

    try {
      await attemptUploadWithRetry(2, 600);
      await saveUploadedChunk(url, index, blob.size);
      const cleanUrl = url.split("?")[0] ?? "";
      setLastUploaded({ index, url: cleanUrl });
      hasUploadedChunksRef.current = true;
      if (recorderError) clearRecorderError();
    } catch (uploadError: any) {
      publishRecorderError({
        type: "s3_recording_fail",
        message: uploadError?.message || "Upload to S3 failed",
        chunkIndex: index,
        retryCount: 3,
      });
      throw uploadError;
    }
  };

  const clearDataRequestTimer = () => {
    if (dataRequestTimerRef.current) {
      window.clearInterval(dataRequestTimerRef.current);
      dataRequestTimerRef.current = null;
    }
  };

  const buildMediaRecorder = (stream: MediaStream): MediaRecorder | null => {
    if (typeof window === "undefined" || typeof window.MediaRecorder === "undefined") {
      publishRecorderError({
        type: "general",
        message: "MediaRecorder is not supported in this browser.",
      });
      return null;
    }

    const options =
      typeof MediaRecorder.isTypeSupported === "function" && MediaRecorder.isTypeSupported(mimeType)
        ? { mimeType }
        : undefined;

    try {
      return options ? new MediaRecorder(stream, options) : new MediaRecorder(stream);
    } catch (err) {
      try {
        return new MediaRecorder(stream);
      } catch (fallbackErr: any) {
        publishRecorderError({
          type: "general",
          message: fallbackErr?.message || "Failed to start recorder for this stream.",
        });
        return null;
      }
    }
  };

  const startRecording = useCallback((recordingId?: string) => {
    clearDataRequestTimer();
    if (!displayMediaStreamRef.current) {
      publishRecorderError({
        type: "general",
        message: "No screen stream available. Call requestShareScreen() first.",
      });
      return;
    }

    const resolvedRecordingId = recordingId || activeRecordingIdRef.current;
    if (!resolvedRecordingId) {
      publishRecorderError({
        type: "general",
        message: "Recording ID is required to start recording.",
      });
      return;
    }

    activeRecordingIdRef.current = resolvedRecordingId;

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try {
        mediaRecorderRef.current.stop();
      } catch {
        // stopping failure should not block new recorder
      }
      mediaRecorderRef.current = null;
    }

    clearDataRequestTimer();
    pendingChunkBufferRef.current = [];
    chunkUploadChainRef.current = Promise.resolve();
    hasUploadedChunksRef.current = false;

    const streamToRecord = compositeStreamRef.current || displayMediaStreamRef.current;
    if (!streamToRecord) {
      publishRecorderError({
        type: "general",
        message: "No available media stream to record.",
      });
      return;
    }

    if (!streamToRecord.active || streamToRecord.getTracks().every((track) => track.readyState === "ended")) {
      publishRecorderError({
        type: "general",
        message: "The shared screen stream is no longer active. Please share your screen again.",
      });
      return;
    }

    const mediaRecorder = buildMediaRecorder(streamToRecord);
    if (!mediaRecorder) {
      return;
    }

    mediaRecorder.onerror = () => {};

    mediaRecorder.ondataavailable = (event: BlobEvent) => {
      if (!event.data || event.data.size === 0) return;

      const now = Date.now();
      const elapsedSinceStart = now - recordingStartTimeRef.current;
      const minChunkTimeMs = (chunkDurationMs || DEFAULT_CHUNK_DURATION_MS) * 0.5;

      // Skip chunks that arrive too early (browser may fire an immediate small chunk)
      if (elapsedSinceStart < minChunkTimeMs) {
        // Buffer this data for the next real chunk
        pendingChunkBufferRef.current.push(event.data);
        return;
      }

      pendingChunkBufferRef.current.push(event.data);
      const chunkBlob = new Blob(pendingChunkBufferRef.current, { type: mimeType });
      pendingChunkBufferRef.current = [];
      lastChunkTimestampRef.current = now;

      const safeChain = (chunkUploadChainRef.current || Promise.resolve()).catch(() => {});
      chunkUploadChainRef.current = safeChain.then(async () => {
        try {
          await uploadChunk(chunkBlob);
        } catch {
          // uploadChunk reports the error; keep chain alive for subsequent chunks
        }
      });
    };

    lastChunkTimestampRef.current = Date.now();
    const chunkIntervalMs = Math.max(500, chunkDurationMs || DEFAULT_CHUNK_DURATION_MS);
    clearDataRequestTimer();
    // Fallback: request data manually if the browser skips time-sliced events
    dataRequestTimerRef.current = window.setInterval(() => {
      if (!mediaRecorderRef.current || mediaRecorderRef.current.state !== "recording") {
        return;
      }
      const elapsed = Date.now() - lastChunkTimestampRef.current;
      const expectedGap = chunkIntervalMs;
      if (elapsed > expectedGap * 1.2) {
        try {
          mediaRecorderRef.current.requestData();
        } catch {
          // ignore requestData failures
        }
      }
    }, chunkIntervalMs);

    try {
      recordingStartTimeRef.current = Date.now();
      mediaRecorder.start(chunkDurationMs);
    } catch (err) {
      publishRecorderError({
        type: "general",
        message: "Failed to start recorder. Browser may not support the selected mimeType.",
      });
      clearDataRequestTimer();
      return;
    }
    mediaRecorderRef.current = mediaRecorder;
    setIsRecording(true);
  }, [chunkDurationMs, mimeType]);

  const stopRecording = useCallback(async () => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === "inactive") {
      setIsRecording(false);
      return;
    }

    const activeRecorder = mediaRecorderRef.current;

    // Request any buffered data before stopping to ensure all audio is captured
    if (activeRecorder.state === "recording") {
      try {
        activeRecorder.requestData();
        // Brief delay to allow the dataavailable event to be processed
        await new Promise((resolve) => setTimeout(resolve, 100));
      } catch {
        // requestData might fail on some browsers, continue anyway
      }
    }

    const waitForStopEvent = () =>
      new Promise<void>((resolve) => {
        const handleStop = () => {
          activeRecorder.removeEventListener("stop", handleStop);
          resolve();
        };
        activeRecorder.addEventListener("stop", handleStop, { once: true });
        try {
          activeRecorder.stop();
        } catch {
          activeRecorder.removeEventListener("stop", handleStop);
          resolve();
        }
      });

    await waitForStopEvent();

    try {
      await (chunkUploadChainRef.current || Promise.resolve());
    } catch {
      // uploadChunk already surfaced error details via publishRecorderError
    } finally {
      clearDataRequestTimer();
      chunkUploadChainRef.current = Promise.resolve();
      pendingChunkBufferRef.current = [];
      mediaRecorderRef.current = null;
      setIsRecording(false);
    }
  }, []);

  const stopAllTracks = useCallback(() => {
    // Stop all media tracks to remove recording indicator from browser tab
    const streams = [
      displayMediaStreamRef.current,
      microphoneMediaStreamRef.current,
      compositeStreamRef.current,
    ];
    streams.forEach((stream) => {
      if (stream) {
        stream.getTracks().forEach((track) => {
          try {
            track.stop();
          } catch {
            // ignore stop failures
          }
        });
      }
    });
    displayMediaStreamRef.current = null;
    microphoneMediaStreamRef.current = null;
    compositeStreamRef.current = null;

    // Close audio context
    if (audioContextInternalRef.current) {
      audioContextInternalRef.current.close().catch(() => {});
      audioContextInternalRef.current = null;
    }
    destinationNodeRef.current = null;
    setIsSharing(false);
  }, []);

  const stopAndMarkComplete = useCallback(async () => {
    await stopRecording();
    stopAllTracks();
    const recordingId = activeRecordingIdRef.current;
    if (recordingId && hasUploadedChunksRef.current) {
      try {
        await markRecordingComplete({ recordingId, kind });
      } catch {
        // ignore completion failures
      }
    } else if (recordingId) {
      // No uploaded chunks to mark complete
    }
  }, [stopRecording, stopAllTracks, kind]);

  useEffect(() => {
    return () => {
      try {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
          mediaRecorderRef.current.stop();
        }
      } catch {
        // swallow failures during forced teardown
      }
      clearDataRequestTimer();

      const displayStream = displayMediaStreamRef.current;
      const microphoneStream = microphoneMediaStreamRef.current;
      const compositeStream = compositeStreamRef.current;

      if (displayStream) {
        displayStream.getTracks().forEach((track) => track.stop());
        displayMediaStreamRef.current = null;
      }
      if (microphoneStream) {
        microphoneStream.getTracks().forEach((track) => track.stop());
        microphoneMediaStreamRef.current = null;
      }
      if (compositeStream) {
        compositeStream.getTracks().forEach((track) => track.stop());
        compositeStreamRef.current = null;
      }

      if (audioContextInternalRef.current) {
        audioContextInternalRef.current.close().catch(() => {});
        audioContextInternalRef.current = null;
      }
      destinationNodeRef.current = null;
      auxConnectionsRef.current.forEach((node) => {
        try {
          node.disconnect();
        } catch {
          // ignore
        }
      });
      auxConnectionsRef.current = [];
      auxAudioStreamRef.current = null;
    };
  }, []);

  return {
    requestShareScreen,
    adoptExistingScreenStream,
    startRecording,
    stopRecording,
    stopAndMarkComplete,
    audioContext: audioContextValue,
    destination: destinationNodeValue,
    isSharing,
    isRecording,
    error: recorderError,
    screenStream: displayMediaStreamRef.current,
    videoRef,
    lastUploaded,
    attachAuxAudioStream,
    attachAudioElement,
  };
};

export default useRecorder;
